import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { OutlinedInput } from "@mui/material";
import { Field, Form, Formik } from "formik";
import jwt_decode from "jwt-decode";
import * as Yup from "yup";

import { axiosInstance } from "api/axios";
import { createAxiosInstance } from "api/axiosConfig";
import { LOG_URL, MFA_CODE_URL, WHO_AM_I } from "api/Service";
import badicon from "assets/images/badicon.svg";
import { Button } from "components/ui/Button";
import { pathGenerators } from "utils/routes";

import { login } from "../../../redux/auth/AuthSlice";
import { AuthLayout } from "../components/AuthLayout/AuthLayout";
import { PasswordInput } from "../components/PasswordInput/PasswordInput";
import s from "./LoginPage.module.css";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Must be a valid email")
    .max(255)
    .required("Email is required"),
  password: Yup.string()
    .max(255)
    .min(6, "Password must be at least 6 characters long")
    .required("Password is required"),
});

const LoginPage = () => {
  const [errMsg, setErrMsg] = useState("");
  const [, setStatus] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showShake, setShowShake] = useState(false);

  const [requires2FA, setRequires2FA] = useState(false);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaRequestId, setMfaRequestId] = useState(null);
  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const mfaInputRefs = useRef([]);

  const [resendCountdown, setResendCountdown] = useState(20);
  const [showResendOptions, setShowResendOptions] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [isResendingSms, setIsResendingSms] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const mfaUserIdRef = useRef(mfaUserId);
  const mfaRequestIdRef = useRef(mfaRequestId);

  useEffect(() => {
    mfaUserIdRef.current = mfaUserId;
  }, [mfaUserId]);

  useEffect(() => {
    mfaRequestIdRef.current = mfaRequestId;
  }, [mfaRequestId]);

  const initialValues = { email: "", password: "" };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  React.useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        const name = await fetchProfileInfo();
        if (name) {
          navigate(pathGenerators.global.home());
        }
      } catch (e) {}
    };

    checkLoggedIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let timer;
    if (requires2FA && resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            setShowResendOptions(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [requires2FA, resendCountdown]);

  const fetchProfileInfo = async () => {
    try {
      const axiosInstance = createAxiosInstance(true);
      const response = await axiosInstance.get(`${WHO_AM_I}`);
      if (response && response.data) {
        if (response.data) {
          let name =
            response && response.data && response.data.staffMember
              ? response.data.staffMember.firstName +
                " " +
                response.data.staffMember.lastName
              : null;
          return name;
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const completeLogin = async (responseData) => {
    const { access_token, refresh_token, userId } = responseData;

    const decodetoken = () => {
      try {
        const decodedValues = jwt_decode(access_token);
        return decodedValues;
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    };
    const decodedToken = await decodetoken();

    setIsSuccess(true);

    sessionStorage.setItem("accessToken", access_token);
    sessionStorage.setItem("refreshToken", refresh_token);
    sessionStorage.setItem("userId", userId);
    sessionStorage.setItem("requestId", responseData.requestId);

    localStorage.setItem("kinesin-accessToken", access_token);
    localStorage.setItem("kinesin-refreshToken", refresh_token);
    localStorage.setItem("kinesin-userId", userId);
    localStorage.setItem("kinesin-requestId", responseData.requestId);
    localStorage.setItem("kinesin-vishvas", decodedToken.tenant);
    localStorage.setItem(
      "kinesin-user",
      JSON.stringify({
        user: {
          id: userId,
          role: decodedToken.role,
          permissions: decodedToken.permissions,
          name: decodedToken.name,
          tenant: decodedToken.tenant,
          type: decodedToken.tenantType,
        },
      })
    );
    localStorage.setItem("kinesin-tenant", decodedToken.tenant);
    const KinesanName = await fetchProfileInfo();
    localStorage.setItem("kinesin-name", KinesanName);
    dispatch(
      login({
        user: {
          id: userId,
          role: decodedToken.role,
          permissions: decodedToken.permissions,
          name: decodedToken.name,
          tenant: decodedToken.tenant,
          type: decodedToken.tenantType,
        },
      })
    );

    setTimeout(() => {
      navigate(pathGenerators.global.home());
    }, 1500);
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setIsLoading(true);
    setShowError(false);
    setErrMsg("");

    try {
      const payload = {
        ...values,
        ...(process.env.REACT_APP_DISABLE_MFA === "true" && {
          skipMfa: true,
        }),
      };

      const response = await axiosInstance.post(LOG_URL, payload, {
        headers: {
          Accept: "*",
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        const data = response.data;

        const mfaDisabled = process.env.REACT_APP_DISABLE_MFA === "true";

        const requires2FA =
          !mfaDisabled &&
          (data.twoAuthRequired ??
            data.two_auth_required ??
            data.twoFactorRequired ??
            data.two_factor_required ??
            data.mfaRequired ??
            data.mfa_required ??
            data.requiresMfa);

        if (requires2FA) {
          mfaUserIdRef.current = data.userId ?? data.user_id;
          mfaRequestIdRef.current = data.requestId ?? data.request_id;
          setMfaUserId(data.userId ?? data.user_id);
          setMfaRequestId(data.requestId ?? data.request_id);
          setRequires2FA(true);
          setResendCountdown(20);
          setShowResendOptions(false);
          setResendMessage("");
          setIsLoading(false);
          setSubmitting(false);
          setTimeout(() => {
            if (mfaInputRefs.current[0]) {
              mfaInputRefs.current[0].focus();
            }
          }, 100);
          return;
        }

        const hasTokens =
          data.access_token ||
          data.accessToken ||
          data.refresh_token ||
          data.refreshToken;
        if (mfaDisabled && !hasTokens) {
          setErrMsg(
            "MFA is required by the server. Set REACT_APP_DISABLE_MFA=false or contact admin."
          );
          return;
        }

        await completeLogin({
          access_token: data.access_token || data.accessToken,
          refresh_token: data.refresh_token || data.refreshToken,
          userId: data.userId,
          requestId: data.requestId,
        });
      } else {
        setErrMsg(response.data.errorMessage);
      }
    } catch (error) {
      setStatus({ type: "error", error });
      setShowError(true);
      setShowShake(true);

      setTimeout(() => {
        setShowShake(false);
      }, 800);

      if (!error?.response) {
        setErrMsg("No Server Response");
      } else if (error.response?.status === 400) {
        setErrMsg("Missing Email or Password");
      } else if (error.response?.status === 401) {
        setErrMsg("Invalid credentials. Please check your email and password.");
      } else {
        setErrMsg("Invalid username / password");
      }
      console.error(error);

      if (error.response?.data?.message) {
        setFieldError("password", error.response.data.message);
      }
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleMfaCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);

    if (value && index < 5) {
      mfaInputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const fullCode = newCode.join("");
      if (fullCode.length === 6) {
        handleMfaSubmit(fullCode);
      }
    }
  };

  const handleMfaKeyDown = (index, e) => {
    if (e.key === "Backspace" && !mfaCode[index] && index > 0) {
      mfaInputRefs.current[index - 1]?.focus();
    }
  };

  const handleMfaPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pastedData) {
      const newCode = [...mfaCode];
      for (let i = 0; i < pastedData.length; i++) {
        newCode[i] = pastedData[i];
      }
      setMfaCode(newCode);

      const nextEmptyIndex = newCode.findIndex((digit) => !digit);
      if (nextEmptyIndex !== -1) {
        mfaInputRefs.current[nextEmptyIndex]?.focus();
      } else {
        mfaInputRefs.current[5]?.focus();
        if (pastedData.length === 6) {
          handleMfaSubmit(pastedData);
        }
      }
    }
  };

  const handleMfaSubmit = async (code) => {
    if (!code || code.length !== 6) {
      setShowError(true);
      setErrMsg("Please enter the complete 6-digit code");
      return;
    }

    const currentUserId = mfaUserIdRef.current;
    const currentRequestId = mfaRequestIdRef.current;

    setIsVerifying(true);
    setShowError(false);
    setErrMsg("");

    try {
      const response = await axiosInstance.post(
        MFA_CODE_URL,
        {
          userId: currentUserId,
          requestId: currentRequestId,
          code: code,
        },
        {
          headers: {
            Accept: "*",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.error) {
        setShowError(true);
        setShowShake(true);
        setErrMsg(
          response.data.errorMessage || "Invalid code. Please try again."
        );
        setMfaCode(["", "", "", "", "", ""]);
        setTimeout(() => setShowShake(false), 800);
        mfaInputRefs.current[0]?.focus();
      } else {
        await completeLogin({
          access_token: response.data.access_token || response.data.accessToken,
          refresh_token:
            response.data.refresh_token || response.data.refreshToken,
          userId: currentUserId,
          requestId: currentRequestId,
        });
      }
    } catch (error) {
      setShowError(true);
      setShowShake(true);
      setErrMsg(
        error.response?.data?.errorMessage || "Invalid code. Please try again."
      );
      setMfaCode(["", "", "", "", "", ""]);
      setTimeout(() => setShowShake(false), 800);
      mfaInputRefs.current[0]?.focus();
      console.error("MFA verification error:", error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setMfaCode(["", "", "", "", "", ""]);
    setMfaUserId(null);
    setMfaRequestId(null);
    setShowError(false);
    setErrMsg("");
    setResendCountdown(20);
    setShowResendOptions(false);
    setResendMessage("");
  };

  const handleResendEmail = async () => {
    if (!mfaUserId || !mfaRequestId) return;

    setIsResendingEmail(true);
    setResendMessage("");
    setShowError(false);

    try {
      const response = await axiosInstance.post(
        "/api/auth/v1/mfacode/resend/email",
        {
          userId: mfaUserId,
          requestId: mfaRequestId,
        },
        {
          headers: {
            Accept: "*",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.requestId) {
        mfaRequestIdRef.current = response.data.requestId;
        setMfaRequestId(response.data.requestId);
      }
      if (response.data?.userId) {
        mfaUserIdRef.current = response.data.userId;
        setMfaUserId(response.data.userId);
      }

      setResendMessage(
        "A new code has been sent to your email. Please check your inbox."
      );
      setResendCountdown(20);
      setShowResendOptions(false);
    } catch (error) {
      setShowError(true);
      setErrMsg(
        error.response?.data?.errorMessage ||
          "Failed to resend code via email. Please try again."
      );
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleResendSms = async () => {
    if (!mfaUserId || !mfaRequestId) return;

    setIsResendingSms(true);
    setResendMessage("");
    setShowError(false);

    try {
      const response = await axiosInstance.post(
        "/api/auth/v1/mfacode/resend/sms",
        {
          userId: mfaUserId,
          requestId: mfaRequestId,
        },
        {
          headers: {
            Accept: "*",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.requestId) {
        mfaRequestIdRef.current = response.data.requestId;
        setMfaRequestId(response.data.requestId);
      }
      if (response.data?.userId) {
        mfaUserIdRef.current = response.data.userId;
        setMfaUserId(response.data.userId);
      }

      setResendMessage(
        "A new code has been sent via SMS. Please check your phone."
      );
      setResendCountdown(20);
      setShowResendOptions(false);
    } catch (error) {
      setShowError(true);
      setErrMsg(
        error.response?.data?.errorMessage ||
          "Failed to resend code via SMS. Please try again."
      );
    } finally {
      setIsResendingSms(false);
    }
  };

  return (
    <AuthLayout>
      <div className={`${s.container} ${showShake ? s.errorShake : ""}`}>
        <div className={s.floatingElements}>
          <div className={s.floatingElement}></div>
          <div className={s.floatingElement}></div>
          <div className={s.floatingElement}></div>
        </div>

        {requires2FA ? (
          <>
            <div className={s.textContainer}>
              <h1 className={s.title}>Two-Factor Authentication</h1>
              <div className={s.secondaryTextContainer}>
                <p>
                  Enter the 6-digit code from your user email address or phone
                </p>
              </div>
            </div>

            <div className={s.form}>
              {showError && errMsg && (
                <div className={s.errorContainer}>
                  <div className={s.errorIcon}>
                    <img src={badicon} alt="Error" />
                  </div>
                  <p className={s.errorText}>{errMsg}</p>
                </div>
              )}

              <div className={s.mfaCodeContainer}>
                {mfaCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (mfaInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleMfaCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleMfaKeyDown(index, e)}
                    onPaste={handleMfaPaste}
                    className={s.mfaInput}
                    autoFocus={index === 0}
                    disabled={isVerifying || isSuccess}
                  />
                ))}
              </div>

              <Button
                className={`${s.submitBtn} ${isVerifying ? s.loading : ""} ${
                  isSuccess ? s.successAnimation : ""
                }`}
                type="button"
                onClick={() => handleMfaSubmit(mfaCode.join(""))}
                disabled={
                  isVerifying || isSuccess || mfaCode.join("").length !== 6
                }
              >
                {isSuccess ? (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        background: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "#01533b",
                      }}
                    >
                      ✓
                    </span>
                    Success!
                  </span>
                ) : isVerifying ? (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTop: "2px solid white",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></span>
                    Verifying...
                  </span>
                ) : (
                  "Verify Code"
                )}
              </Button>

              <div className={s.resendSection}>
                {resendMessage && (
                  <div className={s.resendSuccessMessage}>{resendMessage}</div>
                )}

                {!showResendOptions ? (
                  <div className={s.resendCountdown}>
                    <p>Didn't receive the code? Check your email or SMS.</p>
                    <p className={s.countdownText}>
                      Resend options available in{" "}
                      <span className={s.countdownNumber}>
                        {resendCountdown}
                      </span>{" "}
                      seconds
                    </p>
                  </div>
                ) : (
                  <div className={s.resendOptions}>
                    <p className={s.resendPrompt}>Didn't receive the code?</p>
                    <div className={s.resendButtons}>
                      <Button
                        type="button"
                        variant="outline"
                        className={s.resendButton}
                        onClick={handleResendEmail}
                        disabled={
                          isResendingEmail ||
                          isResendingSms ||
                          isVerifying ||
                          isSuccess
                        }
                      >
                        {isResendingEmail ? (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span
                              style={{
                                width: "14px",
                                height: "14px",
                                border: "2px solid rgba(1,83,59,0.3)",
                                borderTop: "2px solid #01533b",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            ></span>
                            Sending...
                          </span>
                        ) : (
                          "Resend via Email"
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className={s.resendButton}
                        onClick={handleResendSms}
                        disabled={
                          isResendingSms ||
                          isResendingEmail ||
                          isVerifying ||
                          isSuccess
                        }
                      >
                        {isResendingSms ? (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span
                              style={{
                                width: "14px",
                                height: "14px",
                                border: "2px solid rgba(1,83,59,0.3)",
                                borderTop: "2px solid #01533b",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            ></span>
                            Sending...
                          </span>
                        ) : (
                          "Resend via SMS"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                className={s.backButton}
                onClick={handleBackToLogin}
              >
                Back to Login
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={s.textContainer}>
              <h1 className={s.title}>Welcome back to Kinesin</h1>
              <div className={s.secondaryTextContainer}>
                <p>Login to your account.</p>
              </div>
            </div>

            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form className={s.form}>
                  {showError && errMsg && (
                    <div className={s.errorContainer}>
                      <div className={s.errorIcon}>
                        <img src={badicon} alt="Error" />
                      </div>
                      <p className={s.errorText}>{errMsg}</p>
                    </div>
                  )}

                  <div className={s.inputsContainer}>
                    <div className={s.inputWrapper}>
                      <label className={s.label}>Username / Email</label>
                      <Field
                        as={OutlinedInput}
                        sx={{
                          background: "white",
                          borderRadius: "12px",
                          height: "52px",
                          transition: "all 0.3s ease",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#d9d8df",
                            borderWidth: "1.5px",
                          },
                          "&:hover:not(.Mui-focused)": {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#d1d5db",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                            },
                            transform: "translateY(-1px)",
                          },
                          "&.Mui-focused": {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#01533b",
                              borderWidth: "2px",
                              boxShadow: "0 0 0 3px rgba(1, 83, 59, 0.1)",
                            },
                          },
                        }}
                        name="email"
                        type="email"
                        inputProps={{ "data-testid": "login-email" }}
                      />
                      {errors.email && touched.email && (
                        <div className="errorstext">{errors.email}</div>
                      )}
                    </div>

                    <div className={s.inputWrapper}>
                      <label className={s.label} htmlFor="password">
                        Password
                      </label>
                      <PasswordInput
                        id="password"
                        name="password"
                        testId="login-password"
                      />

                      {errors.password && touched.password && (
                        <div className="errorstext">{errors.password}</div>
                      )}

                      <Link
                        className={s.forgotPassword}
                        to={pathGenerators.auth.forgotPassword()}
                      >
                        Forgot your password?
                      </Link>
                    </div>
                  </div>

                  <Button
                    className={`${s.submitBtn} ${isLoading ? s.loading : ""} ${
                      isSuccess ? s.successAnimation : ""
                    }`}
                    type="submit"
                    disabled={isSubmitting || isLoading || isSuccess}
                    data-testid="login-submit"
                  >
                    {isSuccess ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            background: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            color: "#01533b",
                          }}
                        >
                          ✓
                        </span>
                        Success!
                      </span>
                    ) : isLoading ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid white",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        ></span>
                        Signing in...
                      </span>
                    ) : (
                      "Login"
                    )}
                  </Button>
                </Form>
              )}
            </Formik>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export { LoginPage };
